import { useCallback, useEffect, useState } from 'react';

/** 匹配媒体查询，随视口变化自动重渲染（不能用 window.innerWidth 一次性读取） */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false,
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** 断点定义（docs/UI设计.md §1、§7.5） */
export const BREAKPOINTS = {
  /** <1024px 转入移动端形态：底部导航 + 单视图 */
  mobile: '(max-width: 1023px)',
  /** ≥1280px 工作区并排（新闻 | 笔记） */
  workAreaSideBySide: '(min-width: 1280px)',
  /** <1280px 桌面顶栏隐藏指数快切 */
  compactTopBar: '(max-width: 1279px)',
} as const;

export const useIsMobile = () => useMediaQuery(BREAKPOINTS.mobile);

/** 当前时区日期（YYYY-MM-DD），用于交易日判定（Asia/Shanghai） */
export function todayISO(): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(new Date());
}

export const useTodayISO = todayISO;

/**
 * 交易时段判定（docs/UI设计.md §4.3）
 * A股/深市/北交所 09:30–11:30, 13:00–15:00
 * 港股        09:30–12:00, 13:00–16:00
 */
export function isTradingSession(now: Date, market: 'CN' | 'HK' = 'CN'): boolean {
  const shanghai = new Date(
    now.toLocaleString('en-US', { timeZone: 'Asia/Shanghai' }),
  );
  const minutes = shanghai.getHours() * 60 + shanghai.getMinutes();
  const wd = shanghai.getDay();

  if (wd === 0 || wd === 6) return false;

  const [morningEnd, afternoonEnd] = market === 'HK' ? [12 * 60, 16 * 60] : [11 * 60 + 30, 15 * 60];
  const morning = 9 * 60 + 30 <= minutes && minutes <= morningEnd;
  const afternoon = 13 * 60 <= minutes && minutes <= afternoonEnd;
  return morning || afternoon;
}

/** 最近已收盘时刻（分钟数），用于收盘自动重算 */
export function lastCloseMinute(market: 'CN' | 'HK' = 'CN'): number {
  return market === 'HK' ? 16 * 60 : 15 * 60;
}

export function useNow(intervalMs = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  const tick = useCallback(() => setNow(new Date()), []);
  useEffect(() => {
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [intervalMs, tick]);
  return now;
}